import { useEffect, useMemo, useState } from "react";
import { dashboardAPI, meetingAPI, announcementAPI } from "../services/api";
import { useLanguage } from "../context/LanguageContext";
import {
  MdPeople, MdCalendarToday, MdDescription, MdAssignment,
  MdNotifications, MdPersonAdd, MdEventNote, MdCheckCircle,
  MdCampaign, MdLocationOn, MdChevronLeft, MdChevronRight,
} from "react-icons/md";

const BROWN  = "var(--brown)";
const BROWN2 = "var(--brown2)";
const GOLD   = "var(--gold)";
const CREAM  = "var(--cream)";
const BORDER = "var(--border)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const statusColor = {
  DRAFT:"#F59E0B", SCHEDULED:"#3B82F6", CONFIRMED:"#6366F1",
  IN_PROGRESS:"#10B981", COMPLETED:"#6B7280",
  CANCELLED:"#EF4444", POSTPONED:"#F59E0B", RESCHEDULED:"#8B5CF6",
};
const statusLabel = {
  DRAFT:"សេចក្តីព្រាង", SCHEDULED:"កំណត់ហើយ", CONFIRMED:"បានបញ្ជាក់",
  IN_PROGRESS:"កំពុងប្រជុំ", COMPLETED:"បានបញ្ចប់",
  CANCELLED:"បោះបង់", POSTPONED:"ពន្យារ", RESCHEDULED:"ចាក់ឡើងវិញ",
};

const priorityColor = { LOW:"#10B981", MEDIUM:"#3B82F6", HIGH:"#F59E0B", URGENT:"#EF4444" };
const priorityLabel = { LOW:"ទាប", MEDIUM:"មធ្យម", HIGH:"ខ្ពស់", URGENT:"បន្ទាន់" };

const WEEKDAY_KH = ["អាទិត្យ","ចន្ទ","អង្គារ","ពុធ","ព្រហស្បតិ៍","សុក្រ","សៅរ៍"];

const toISODate = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,"0"), day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
};

const parseISODate = (iso) => new Date(`${iso}T00:00:00`);

const addDays = (iso, n) => {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + n);
  return toISODate(d);
};

// "ថ្ងៃនេះ" / "ថ្ងៃស្អែក" / "ម្សិលមិញ" for the days right around today,
// otherwise the weekday name + numeric date.
function dayLabel(iso, t) {
  const today = toISODate(new Date());
  if (iso === today) return t("ថ្ងៃនេះ");
  if (iso === addDays(today, 1)) return t("ថ្ងៃស្អែក");
  if (iso === addDays(today, -1)) return t("ម្សិលមិញ");
  const d = parseISODate(iso);
  return `${t(WEEKDAY_KH[d.getDay()])} — ${d.toLocaleDateString("en-GB")}`;
}

function Skeleton({ h = 16, w = "100%" }) {
  return (
    <div style={{ height:h, width:w, background:"var(--border)",
                  borderRadius:6, animation:"pulse 1.4s ease infinite" }} />
  );
}

function StatCard({ label, value, sub, subColor, icon, loading, accent, onClick }) {
  return (
    <div style={{
      background:"var(--surface)", borderRadius:14, padding:"20px 22px",
      border:`1px solid ${BORDER}`,
      borderTop:`3px solid ${accent || GOLD}`,
      transition:"box-shadow 0.2s",
      cursor: onClick ? "pointer" : "default",
    }}
      onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 20px rgba(92,45,14,0.1)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow="none"}>
      <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"flex-start", marginBottom:12 }}>
        <p style={{ fontSize:12, color:MUTED, fontFamily:KH, fontWeight:500 }}>{label}</p>
        <span style={{ display:"flex", alignItems:"center", color:accent || GOLD }}>{icon}</span>
      </div>
      {loading
        ? <Skeleton h={36} w="60%" />
        : <p style={{ fontSize:34, fontWeight:700, color:BROWN2,
                      fontFamily:MO, marginBottom:6 }}>{value ?? "—"}</p>
      }
      {loading
        ? <Skeleton h={12} w="50%" />
        : <p style={{ fontSize:11, color:subColor || MUTED, fontFamily:KH }}>{sub}</p>
      }
    </div>
  );
}

function SectionHeader({ title, link, onLinkClick }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between",
                  alignItems:"center", marginBottom:16 }}>
      <h3 style={{ fontSize:14, fontWeight:700, color:BROWN2, fontFamily:MO }}>{title}</h3>
      {link && (
        <span onClick={onLinkClick}
          style={{ fontSize:11, color:GOLD, cursor:"pointer", fontFamily:KH,
                       padding:"3px 10px", borderRadius:20,
                       border:`1px solid ${BORDER}` }}>
          {link}
        </span>
      )}
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const { t } = useLanguage();
  const [summary,     setSummary]     = useState(null);
  const [allMeetings, setAllMeetings] = useState([]);
  const [news,        setNews]        = useState([]);
  const [loading,     setLoading]     = useState(true);

  const todayISO = useMemo(() => toISODate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(todayISO);

  useEffect(() => {
    const load = async () => {
      try {
        const [sumRes, meetRes, newsRes] = await Promise.allSettled([
          dashboardAPI.getSummary(),
          meetingAPI.getAll(0, 200),
          announcementAPI.getAll(0, 4),
        ]);
        if (sumRes.status  === "fulfilled") setSummary(sumRes.value?.data?.data   ?? sumRes.value?.data);
        if (meetRes.status === "fulfilled") setAllMeetings(meetRes.value?.data?.data?.content || meetRes.value?.data?.content || []);
        if (newsRes.status === "fulfilled") setNews(newsRes.value?.data?.data?.content    || newsRes.value?.data?.content || []);
      } catch (e) { console.error("Dashboard:", e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const selectedLabel = dayLabel(selectedDate, t);

  const meetings = allMeetings
    .filter(m => m.meetingDate === selectedDate)
    .sort((a,b) => (a.startTime||"").localeCompare(b.startTime||""));

  const stats = [
    { label:t("មន្ត្រីសរុប"),       value:summary?.totalOfficers,        sub:`${summary?.totalContractOfficers ?? 0} ${t("កិច្ចសន្យា")}`, subColor:"#10B981", icon:<MdPeople size={20} />, accent:"#10B981", nav:"staff" },
    { label:t("ប្រជុំថ្ងៃនេះ"),    value:summary?.todayMeetings,         sub:t("ថ្ងៃនេះ"),               icon:<MdCalendarToday size={20} />, accent:"#3B82F6", nav:"meeting-list" },
    { label:t("ឯកសារជិតផុត"),     value:summary?.expiringDocuments,     sub:t("ជិតផុតកំណត់"),           subColor:"#F59E0B", icon:<MdDescription size={20} />, accent:"#F59E0B", nav:"doc-officers" },
    { label:t("កិច្ចសន្យាជិតផុត"),  value:summary?.expiringContracts,    sub:t("ក្នុង ៣០ ថ្ងៃ"),          subColor:"#EF4444", icon:<MdAssignment size={20} />, accent:"#EF4444", nav:"contract" },
    { label:t("ការជូនដំណឹង"),      value:summary?.unreadNotifications,   sub:t("មិនទាន់អាន"),             subColor:"#8B5CF6", icon:<MdNotifications size={20} />, accent:"#8B5CF6", nav:"notifications" },
  ];

  const progressItems = [
    { label:t("មន្ត្រី"), v:summary?.totalOfficers||0, t:Math.max(summary?.totalOfficers||0,10), color:"#10B981", nav:"staff" },
    { label:t("ឯកសារជិតផុត"), v:summary?.expiringDocuments||0, t:Math.max(summary?.expiringDocuments||0,10), color:"#F59E0B", nav:"doc-officers" },
    { label:t("ប្រជុំថ្ងៃនេះ"), v:summary?.todayMeetings||0, t:Math.max(summary?.todayMeetings||0,5), color:"#3B82F6", nav:"meeting-list" },
  ];

  return (
    <div style={{ background:CREAM, minHeight:"100%" }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        .qa-btn:hover { background:var(--border) !important; transform:translateY(-1px); }
        .news-row:hover { background:${CREAM} !important; }
      `}</style>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)",
                    gap:12, marginBottom:24 }}>
        {stats.map((s,i) => (
          <StatCard key={i} {...s} loading={loading}
            onClick={s.nav ? () => onNavigate && onNavigate(s.nav) : undefined} />
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 0.75fr", gap:16 }}>

        <div style={{ background:"var(--surface)", borderRadius:14, padding:20,
                      border:`1px solid ${BORDER}` }}>
          <SectionHeader title={`${t("ប្រជុំ")} — ${selectedLabel}`}
            link={t("មើលទាំងអស់")} onLinkClick={() => onNavigate && onNavigate("meeting-list")} />

          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:14 }}>
            <button onClick={() => setSelectedDate(d => addDays(d, -1))}
              style={{
                width:26, height:26, borderRadius:"50%", cursor:"pointer",
                border:`1px solid ${BORDER}`, background:"var(--surface)", color:MUTED,
                display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
              }}>
              <MdChevronLeft size={16} />
            </button>

            <span style={{
              flex:1, textAlign:"center", fontSize:12, fontFamily:KH, fontWeight:600,
              color: selectedDate===todayISO ? "#fff" : BROWN,
              background: selectedDate===todayISO ? BROWN : "transparent",
              padding: selectedDate===todayISO ? "5px 10px" : 0,
              borderRadius:20,
            }}>
              {selectedLabel}
            </span>

            <button onClick={() => setSelectedDate(d => addDays(d, 1))}
              style={{
                width:26, height:26, borderRadius:"50%", cursor:"pointer",
                border:`1px solid ${BORDER}`, background:"var(--surface)", color:MUTED,
                display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
              }}>
              <MdChevronRight size={16} />
            </button>

            {selectedDate !== todayISO && (
              <button onClick={() => setSelectedDate(todayISO)}
                style={{
                  padding:"5px 10px", borderRadius:20, cursor:"pointer",
                  fontSize:11, fontFamily:KH, whiteSpace:"nowrap",
                  border:`1px solid ${BORDER}`, background:"var(--surface)", color:MUTED,
                }}>
                {t("ថ្ងៃនេះ")}
              </button>
            )}

            <input type="date" value={selectedDate}
              onChange={e => e.target.value && setSelectedDate(e.target.value)}
              style={{
                padding:"5px 8px", borderRadius:20, cursor:"pointer",
                fontSize:11, fontFamily:KH,
                border:`1px solid ${BORDER}`, background:"var(--surface)", color:MUTED,
              }} />
          </div>
          {loading ? (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {[1,2,3].map(i => <Skeleton key={i} h={48} />)}
            </div>
          ) : meetings.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 0", color:MUTED }}>
              <div style={{ fontSize:28, marginBottom:8, display:"flex", justifyContent:"center" }}><MdCalendarToday size={32} /></div>
              <p style={{ fontSize:12, fontFamily:KH }}>{t("គ្មានប្រជុំ")}</p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {meetings.map((m,i) => {
                const sc = statusColor[m.statusCode||m.status] || "#94A3B8";
                const sl = t(statusLabel[m.statusCode||m.status] || m.status);
                return (
                  <div key={i} style={{
                    display:"flex", gap:12, alignItems:"flex-start",
                    padding:"10px 12px", borderRadius:10,
                    borderLeft:`3px solid ${sc}`,
                    background:sc+"0d", marginBottom:4,
                    cursor:"pointer", transition:"all 0.15s",
                  }}
                    onClick={() => onNavigate && onNavigate("meeting-list")}
                    className="news-row">
                    <span style={{ fontSize:11, color:MUTED, minWidth:38,
                                   paddingTop:1, fontFamily:KH, flexShrink:0 }}>
                      {m.startTime?.slice(0,5) || "—"}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:13, fontWeight:500, color:BROWN2,
                                  fontFamily:KH, marginBottom:3,
                                  overflow:"hidden", textOverflow:"ellipsis",
                                  whiteSpace:"nowrap" }}>
                        {m.title || t("ប្រជុំ")}
                      </p>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10,
                                       fontWeight:600, background:sc+"22", color:sc,
                                       fontFamily:KH }}>
                          {sl}
                        </span>
                        {m.roomName && (
                          <span style={{ fontSize:10, color:MUTED, fontFamily:KH, display:"flex", alignItems:"center", gap:2 }}>
                            <MdLocationOn size={11} /> {m.roomName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ background:"var(--surface)", borderRadius:14, padding:20,
                      border:`1px solid ${BORDER}` }}>
          <SectionHeader title={t("សេចក្តីប្រកាសថ្មី")} link={t("មើលទាំងអស់")}
            onLinkClick={() => onNavigate && onNavigate("ann-list")} />
          {loading ? (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {[1,2,3].map(i => <Skeleton key={i} h={56} />)}
            </div>
          ) : news.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 0", color:MUTED }}>
              <div style={{ fontSize:28, marginBottom:8, display:"flex", justifyContent:"center" }}><MdCampaign size={32} /></div>
              <p style={{ fontSize:12, fontFamily:KH }}>{t("គ្មានការប្រកាស")}</p>
            </div>
          ) : (
            news.map((n,i) => {
              const pc = priorityColor[n.priority] || "#3B82F6";
              const pl = t(priorityLabel[n.priority] || n.priority);
              return (
                <div key={i} className="news-row"
                  onClick={() => onNavigate && onNavigate("ann-list")}
                  style={{
                  padding:"12px 0",
                  borderBottom: i < news.length-1 ? `1px solid ${BORDER}` : "none",
                  cursor:"pointer", borderRadius:6, transition:"all 0.15s",
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between",
                                alignItems:"flex-start", gap:8 }}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:13, fontWeight:500, color:BROWN2,
                                  fontFamily:KH, marginBottom:4, lineHeight:1.5 }}>
                        {n.title || "—"}
                      </p>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:10, color:MUTED, fontFamily:KH }}>
                          {n.createdByName || "—"}
                        </span>
                        <span style={{ fontSize:10, color:MUTED }}>·</span>
                        <span style={{ fontSize:10, color:MUTED, fontFamily:KH }}>
                          {n.createdAt?.slice(0,10) || "—"}
                        </span>
                      </div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column",
                                  alignItems:"flex-end", gap:4, flexShrink:0 }}>
                      <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10,
                                     fontWeight:600, background:pc+"22", color:pc,
                                     fontFamily:KH }}>
                        {pl}
                      </span>
                      {n.status && (
                        <span style={{ fontSize:10, color:MUTED, fontFamily:KH }}>
                          {n.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          <div style={{ background:"var(--surface)", borderRadius:14, padding:20,
                        border:`1px solid ${BORDER}` }}>
            <SectionHeader title={t("សកម្មភាពរហ័ស")} />
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                { label:t("បន្ថែមមន្ត្រី"), icon:<MdPersonAdd   size={22} />, key:"staff",       color:"#10B981" },
                { label:t("បន្ថែមប្រជុំ"),  icon:<MdEventNote   size={22} />, key:"meeting-list", color:"#3B82F6" },
                { label:t("អនុម័ត"),         icon:<MdCheckCircle size={22} />, key:"doc-approval", color:"#6366F1" },
                { label:t("ការប្រកាស"),      icon:<MdCampaign    size={22} />, key:"ann-list",     color:"#F59E0B" },
              ].map((a,i) => (
                <button key={i} className="qa-btn"
                  onClick={() => onNavigate && onNavigate(a.key)}
                  style={{
                    background:CREAM, border:`1px solid ${BORDER}`,
                    borderRadius:10, padding:"14px 6px", cursor:"pointer",
                    display:"flex", flexDirection:"column",
                    alignItems:"center", gap:6, transition:"all 0.15s",
                    width:"100%",
                  }}>
                  <span style={{ display:"flex", alignItems:"center", color:a.color }}>{a.icon}</span>
                  <span style={{ fontSize:11, color:BROWN, fontFamily:KH,
                                 textAlign:"center", lineHeight:1.4,
                                 fontWeight:500 }}>
                    {a.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ background:"var(--surface)", borderRadius:14, padding:20,
                        border:`1px solid ${BORDER}`, flex:1 }}>
            <SectionHeader title={t("សង្ខេបស្ថានភាព")} />
            {loading ? (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {[1,2,3].map(i => <Skeleton key={i} h={20} />)}
              </div>
            ) : (
              progressItems.map((b,i) => (
                <div key={i}
                  onClick={() => onNavigate && onNavigate(b.nav)}
                  style={{ marginBottom: i < progressItems.length-1 ? 14 : 0, cursor:"pointer" }}>
                  <div style={{ display:"flex", justifyContent:"space-between",
                                fontSize:11, color:MUTED, marginBottom:5, fontFamily:KH }}>
                    <span>{b.label}</span>
                    <span style={{ fontWeight:600, color:BROWN }}>
                      {b.v}/{b.t}
                    </span>
                  </div>
                  <div style={{ background:"var(--border)", borderRadius:6, height:6 }}>
                    <div style={{
                      width:`${b.t ? Math.min(Math.round(b.v/b.t*100),100) : 0}%`,
                      background:b.color, borderRadius:6, height:"100%",
                      transition:"width 0.6s cubic-bezier(0.4,0,0.2,1)",
                    }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}